import { apiFetch } from '@ami/react/core';
import { ProductFilter } from '../products/productFilter';
import { ThumbProductDetail } from '../products/thumbProductDetail';

export const ProductDimensionType = {
  singleLine: 1,
  multiLine: 2,
};

export const ProductPartNumberLogic = Object.freeze({
  AllowCouplerPartNumberToBeAppended:
    'Allow Coupler part number to be appended',
  DoNotAllowCouplerPartNumberToBeAppended:
    'Do not allow Coupler part number to be appended',
});

export class ProductOptionSetQuantity {
  constructor(json) {
    this.optionSetId = json.optionSetId;
    this.quantity = json.quantity;
  }
}

export class ProductDimension {
  constructor(json) {
    this.dimensionId = json.dimensionId;
    this.type = json.type;
    this.name = json.name;
    this.value = json.value;
    this.unitContraction = json.unitContraction;
    this.isSystem = json.isSystem;
    this.category = json.category;
  }
}

export const ProductVisibility = Object.freeze({
  VisiblePublic: 'Visible to AMI and Dealers',
  VisibleInternalRestrictedPublic: 'Visible to AMI, Restricted to Dealers',
  VisibleInternal: 'Visible to AMI',
  Hidden: 'Hidden',
});

const _baseEndPoint = 'products';
export class Product {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.callForLeadTime = item.callForLeadTime;
      this.standardLeadTimeId = item.standardLeadTimeId;
      this.standardLeadTimeDescription = item.standardLeadTimeDescription;
      this.fastTrackLeadTimeId = item.fastTrackLeadTimeId;
      this.fastTrackLeadTimeDescription = item.fastTrackLeadTimeDescription;
      this.fullDesignLeadTimeId = item.fullDesignLeadTimeId;
      this.fullQueueLeadTimeId = item.fullQueueLeadTimeId;
      this.partialDesignLeadTimeId = item.partialDesignLeadTimeId;
      this.partialQueueLeadTimeId = item.partialQueueLeadTimeId;
      this.luggingOnlyDesignLeadTimeId = item.luggingOnlyDesignLeadTimeId;
      this.luggingOnlyQueueLeadTimeId = item.luggingOnlyQueueLeadTimeId;
      this.processingOnlyDesignLeadTimeId = item.processingOnlyDesignLeadTimeId;
      this.processingOnlyQueueLeadTimeId = item.processingOnlyQueueLeadTimeId;
      this.partNumber = item.partNumber;
      this.partNumberLogic = item.partNumberLogic;
      this.productGroupId = item.productGroupId;
      this.productGroupName = item.productGroupName;
      this.productGroupTypeName = item.productGroupTypeName;
      this.dimensions = item.dimensions.map((x) => new ProductDimension(x));
      this.optionSetQuantities = item.optionSetQuantities.map(
        (x) => new ProductOptionSetQuantity(x)
      );
      this.carrierId = item.carrierId;
      this.carrierName = item.carrierName;
      this.attachmentPointId = item.attachmentPointId;
      this.weightCategoryIds = item.weightCategoryIds;
      this.weightCategoryNames = item.weightCategoryNames;
      this.price = item.price;
      this.visibility = item.visibility;
      this.detail = item.detail ? new ThumbProductDetail(item.detail) : null;
      return;
    }
    this.id = null;
    this.callForLeadTime = false;
    this.standardLeadTimeId = null;
    this.standardLeadTimeDescription = '';
    this.fastTrackLeadTimeId = null;
    this.fastTrackLeadTimeDescription = '';
    this.fullDesignLeadTimeId = null;
    this.fullQueueLeadTimeId =  null;
    this.partialDesignLeadTimeId = null;
    this.partialQueueLeadTimeId = null;
    this.luggingOnlyDesignLeadTimeId = null;
    this.luggingOnlyQueueLeadTimeId = null;
    this.processingOnlyDesignLeadTimeId = null;
    this.processingOnlyQueueLeadTimeId = null;
    this.partNumber = '';
    this.partNumberLogic = 'AllowCouplerPartNumberToBeAppended';
    this.productGroupId = null;
    this.productGroupName = '';
    this.productGroupTypeName = '';
    this.dimensions = [];
    this.optionSetQuantities = [];
    this.carrierId = null;
    this.carrierName = '';
    this.attachmentPointId = null;
    this.weightCategoryIds = [];
    this.weightCategoryNames = '';
    this.price = '';
    this.visibility = 'VisiblePublic';
    this.detail = null;
  }

  static async search(filter = new ProductFilter(), page) {
    filter.setPaginationParameters(page);
    const entities = await apiFetch(
      `${_baseEndPoint}?${filter.toQueryString()} `
    );
    return entities.map((x) => new Product(x));
  }

  static async getById(id) {
    const entity = await apiFetch(`${_baseEndPoint}/${id}`);
    return new Product(entity);
  }

  static async copyFrom(id) {
    const json = await apiFetch(`${_baseEndPoint}/copy-from/${id}`);
    return new Product(json);
  }

  static create(item) {
    return apiFetch(_baseEndPoint, 'POST', item);
  }

  static async getForExport(filter = new ProductFilter()) {
    return await apiFetch(
      `${_baseEndPoint}/export/csv?${filter.toQueryString()}`
    );
  }

  update() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'PUT', this);
  }

  delete() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'DELETE', this);
  }
}
